import React from 'react';

const ConsistentEnvironmentsSection = () => {
  return (
    <section className="py-24 md:py-32 px-4 md:px-6 lg:px-8" style={{ backgroundColor: '#242423' }}>
      <div className="max-w-7xl mx-auto">
        
        {/* Side-by-side layout using inline flexbox */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '64px', alignItems: 'center' }}>
          
          {/* Left Column - Text Content */}
          <div style={{ flex: '1 1 400px', minWidth: 0 }}>
            
            {/* Icon + Title Row */}
            <div className="flex items-center gap-4 mb-6">
              <div 
                className="rounded-lg flex items-center justify-center"
                style={{ 
                  width: '56px', 
                  height: '56px', 
                  backgroundColor: 'rgba(79, 179, 184, 0.15)' 
                }}
              >
                {/* Layers SVG Icon - 3 stacked diamond paths */}
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#4FB3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="12 2 2 7 12 12 22 7 12 2" />
                  <polyline points="2 17 12 22 22 17" />
                  <polyline points="2 12 12 17 22 12" />
                </svg>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold" style={{ color: '#E8EDDF' }}>
                Consistent Environments
              </h2>
            </div>
            
            {/* Description Paragraph */}
            <p className="text-base md:text-lg mb-8" style={{ color: '#9CA3AF' }}>
              Same orchestration layer in dev, staging, and production. Identical agent behavior across laptop simulations and industrial deployments. Infrastructure-as-code for reproducible environments at any scale with deterministic deployment semantics.
            </p>
            
            {/* Three Bullet Points */}
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div 
                  className="rounded-full mt-1.5" 
                  style={{ 
                    width: '8px', 
                    height: '8px', 
                    backgroundColor: '#4FB3B8',
                    flexShrink: 0
                  }}
                ></div>
                <span className="text-base" style={{ color: '#E8EDDF' }}>
                  Identical orchestration layer across all environments
                </span>
              </li>
              <li className="flex items-start gap-3">
                <div 
                  className="rounded-full mt-1.5" 
                  style={{ 
                    width: '8px', 
                    height: '8px', 
                    backgroundColor: '#4FB3B8',
                    flexShrink: 0
                  }}
                ></div>
                <span className="text-base" style={{ color: '#E8EDDF' }}>
                  Deterministic deployment semantics
                </span>
              </li>
              <li className="flex items-start gap-3">
                <div 
                  className="rounded-full mt-1.5" 
                  style={{ 
                    width: '8px', 
                    height: '8px', 
                    backgroundColor: '#4FB3B8',
                    flexShrink: 0
                  }}
                ></div>
                <span className="text-base" style={{ color: '#E8EDDF' }}>
                  Infrastructure-as-code for reproducible setups
                </span>
              </li>
            </ul>
          </div>
          
          {/* Right Column - Diagram Card */}
          <div style={{ flex: '1 1 400px', minWidth: 0 }}>
            <div 
              className="rounded-xl p-8"
              style={{ 
                backgroundColor: '#2E2E2C', 
                border: '1px solid #3A3A38' 
              }}
            >
              
              {/* ENVIRONMENT PARITY Badge */}
              <div className="flex justify-center mb-8">
                <div 
                  className="px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider"
                  style={{ 
                    backgroundColor: 'transparent', 
                    border: '1px solid #4FB3B8', 
                    color: '#4FB3B8' 
                  }}
                >
                  ENVIRONMENT PARITY
                </div>
              </div>
              
              {/* Development Card */}
              <div 
                className="rounded-lg p-5 mb-6"
                style={{ 
                  backgroundColor: '#242423', 
                  border: '1px solid #4FB3B8',
                  position: 'relative'
                }}
              >
                {/* Number Badge "1" */}
                <div 
                  className="absolute flex items-center justify-center font-bold"
                  style={{ 
                    left: '-12px', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    width: '24px', 
                    height: '24px', 
                    borderRadius: '50%',
                    backgroundColor: '#4FB3B8', 
                    color: '#242423',
                    fontSize: '12px'
                  }}
                >
                  1
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {/* Terminal Icon */}
                    <div 
                      className="rounded-lg flex items-center justify-center"
                      style={{ 
                        width: '48px', 
                        height: '48px', 
                        backgroundColor: 'rgba(79, 179, 184, 0.15)' 
                      }}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4FB3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="4 17 10 11 4 5" />
                        <line x1="12" y1="19" x2="20" y2="19" />
                      </svg>
                    </div>
                    
                    <div>
                      <div className="font-bold text-base" style={{ color: '#E8EDDF' }}>
                        Development
                      </div>
                      <div className="text-sm" style={{ color: '#9CA3AF' }}>
                        Local laptop simulation
                      </div>
                    </div>
                  </div>
                  
                  {/* Active Status */}
                  <div className="flex items-center gap-2">
                    <div 
                      className="rounded-full" 
                      style={{ 
                        width: '8px', 
                        height: '8px', 
                        backgroundColor: '#4FB3B8' 
                      }}
                    ></div>
                    <span className="text-sm font-medium" style={{ color: '#4FB3B8' }}>
                      Active
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Teal Down Arrow */}
              <div className="flex justify-center mb-6">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4FB3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <polyline points="19 12 12 19 5 12" />
                </svg>
              </div>
              
              {/* Staging Card */}
              <div 
                className="rounded-lg p-5 mb-6"
                style={{ 
                  backgroundColor: '#242423', 
                  border: '1px solid #4FB3B8',
                  position: 'relative'
                }}
              >
                {/* Number Badge "2" */}
                <div 
                  className="absolute flex items-center justify-center font-bold"
                  style={{ 
                    left: '-12px', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    width: '24px', 
                    height: '24px', 
                    borderRadius: '50%',
                    backgroundColor: '#4FB3B8', 
                    color: '#242423',
                    fontSize: '12px'
                  }}
                >
                  2
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {/* Grid Icon */}
                    <div 
                      className="rounded-lg flex items-center justify-center"
                      style={{ 
                        width: '48px', 
                        height: '48px', 
                        backgroundColor: 'rgba(79, 179, 184, 0.15)' 
                      }}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4FB3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="7" height="7" />
                        <rect x="14" y="3" width="7" height="7" />
                        <rect x="14" y="14" width="7" height="7" />
                        <rect x="3" y="14" width="7" height="7" />
                      </svg>
                    </div>
                    
                    <div>
                      <div className="font-bold text-base" style={{ color: '#E8EDDF' }}>
                        Staging
                      </div>
                      <div className="text-sm" style={{ color: '#9CA3AF' }}>
                        Cloud test cluster
                      </div>
                    </div>
                  </div>
                  
                  {/* Active Status */}
                  <div className="flex items-center gap-2">
                    <div 
                      className="rounded-full" 
                      style={{ 
                        width: '8px', 
                        height: '8px', 
                        backgroundColor: '#4FB3B8' 
                      }}
                    ></div>
                    <span className="text-sm font-medium" style={{ color: '#4FB3B8' }}>
                      Active
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Teal Down Arrow */}
              <div className="flex justify-center mb-6">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4FB3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <polyline points="19 12 12 19 5 12" />
                </svg>
              </div>
              
              {/* Production Card */}
              <div 
                className="rounded-lg p-5 mb-8"
                style={{ 
                  backgroundColor: '#242423', 
                  border: '1px solid #4FB3B8',
                  position: 'relative'
                }}
              >
                {/* Number Badge "3" */}
                <div 
                  className="absolute flex items-center justify-center font-bold"
                  style={{ 
                    left: '-12px', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    width: '24px', 
                    height: '24px', 
                    borderRadius: '50%',
                    backgroundColor: '#4FB3B8', 
                    color: '#242423',
                    fontSize: '12px'
                  }}
                >
                  3
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {/* Server Icon */}
                    <div 
                      className="rounded-lg flex items-center justify-center"
                      style={{ 
                        width: '48px', 
                        height: '48px', 
                        backgroundColor: 'rgba(79, 179, 184, 0.15)' 
                      }}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4FB3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
                        <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
                        <line x1="6" y1="6" x2="6.01" y2="6" />
                        <line x1="6" y1="18" x2="6.01" y2="18" />
                      </svg>
                    </div>
                    
                    <div>
                      <div className="font-bold text-base" style={{ color: '#E8EDDF' }}>
                        Production
                      </div>
                      <div className="text-sm" style={{ color: '#9CA3AF' }}>
                        Edge industrial deployment
                      </div>
                    </div>
                  </div>
                  
                  {/* Live Status - GREEN */}
                  <div className="flex items-center gap-2">
                    <div 
                      className="rounded-full" 
                      style={{ 
                        width: '8px', 
                        height: '8px', 
                        backgroundColor: '#27CA40' 
                      }}
                    ></div>
                    <span className="text-sm font-medium" style={{ color: '#27CA40' }}>
                      Live
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Identical Sleeve Orchestration Layer Card */}
              <div 
                className="rounded-lg p-6 text-center"
                style={{ 
                  border: '2px solid #F5CB5C'
                }}
              >
                {/* Layers Icon in Saffron */}
                <div className="flex justify-center mb-3">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#F5CB5C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="12 2 2 7 12 12 22 7 12 2" />
                    <polyline points="2 17 12 22 22 17" />
                    <polyline points="2 12 12 17 22 12" />
                  </svg>
                </div>
                
                <div className="font-bold text-base mb-2" style={{ color: '#E8EDDF' }}>
                  Identical Sleeve Orchestration Layer
                </div>
                
                <div className="text-xs" style={{ color: '#9CA3AF' }}>
                  Same behavior • Deterministic deployment • Infrastructure-as-code
                </div>
              </div>
              
            </div>
          </div>
          
        </div>
        
      </div>
    </section>
  );
};

export default ConsistentEnvironmentsSection;